import * as React from 'react';
import { noop } from 'vet-bones/utils';

import { generateModelNamedGraph } from 'src/containers/ProjectDiagram/utils/generateProjectIri';
import { projectSelector } from 'src/selectors/project';
import { PendingModelData } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type UploadModelContextValue = {
  databaseId: string;
  file: File | null;
  isModelDataLoading: boolean;
  isNameEdited: boolean;
  isNamedGraphIriEdited: boolean;
  name: string;
  namedGraphIri: string;
  pendingModelData: PendingModelData | null;
  setDatabaseId: (database: string) => void;
  setFile: (file: File | null) => void;
  setIsModelDataLoading: (loading: boolean) => void;
  setIsNameEdited: (value: boolean) => void;
  setIsNamedGraphIriEdited: (value: boolean) => void;
  setName: (modelName: string) => void;
  setNamedGraphIri: (namespace: string) => void;
  setPendingModelData: (data: PendingModelData | null) => void;
};

export const UploadModelContext = React.createContext<UploadModelContextValue>({
  databaseId: '',
  file: null,
  isModelDataLoading: false,
  isNameEdited: false,
  isNamedGraphIriEdited: false,
  name: '',
  namedGraphIri: '',
  pendingModelData: null,
  setDatabaseId: noop,
  setFile: noop,
  setIsModelDataLoading: noop,
  setIsNameEdited: noop,
  setIsNamedGraphIriEdited: noop,
  setName: noop,
  setNamedGraphIri: noop,
  setPendingModelData: noop,
});

export const useUploadModel = () => {
  return React.useContext(UploadModelContext);
};

export const useUploadModelValue = (): UploadModelContextValue => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const [databaseId, setDatabaseId] = React.useState<string>('');
  const [file, setFile] = React.useState<File | null>(null);
  const [name, setNameFn] = React.useState(
    copy.components.projectModelDialog.localModelPanel.nameExampleText
  );
  const [namedGraphIri, setNamedGraphIriFn] = React.useState(() =>
    project ? generateModelNamedGraph(project, { name: 'example' }) : ''
  );
  const [pendingModelData, setPendingModelData] =
    React.useState<PendingModelData | null>(null);
  const [isModelDataLoading, setIsModelDataLoading] =
    React.useState<boolean>(false);
  const [isNameEdited, setIsNameEdited] = React.useState(false);
  const [isNamedGraphIriEdited, setIsNamedGraphIriEdited] =
    React.useState(false);

  const setName = React.useCallback(
    (modelName: string) => {
      if (modelName === name) {
        return;
      }

      setNameFn(modelName);
      setIsNameEdited(true);
      if (!isNamedGraphIriEdited && modelName && project) {
        setNamedGraphIriFn(
          generateModelNamedGraph(project, { name: modelName })
        );
      } else if (!modelName) {
        setNameFn(
          copy.components.projectModelDialog.localModelPanel.nameExampleText
        );
        if (project) {
          setNamedGraphIriFn(
            generateModelNamedGraph(project, { name: 'example' })
          );
        }
      }
    },
    [isNamedGraphIriEdited, name, project]
  );

  const setNamedGraphIri = React.useCallback(
    (value: string) => {
      if (value === namedGraphIri) {
        return;
      }

      if (value) {
        setIsNamedGraphIriEdited(true);
      }
      setNamedGraphIriFn(value);
    },
    [namedGraphIri]
  );

  return {
    databaseId,
    file,
    isModelDataLoading,
    isNameEdited,
    isNamedGraphIriEdited,
    name,
    namedGraphIri,
    pendingModelData,
    setDatabaseId,
    setFile,
    setIsModelDataLoading,
    setIsNameEdited,
    setIsNamedGraphIriEdited,
    setName,
    setNamedGraphIri,
    setPendingModelData,
  };
};
